import type { MaterialInfo, PartDefinition } from '@rogue/rackbuilder'
import { CaveOptions, ShelfConfig } from '../../RackTypes'
import { MaterialBuilder } from '../rack/builders/materialBuilder'
import { CaveAccessoryBuilder } from './parts/caveAccessoryParts'
import { CaveCrossmemberBuilder } from './parts/caveCrossmemberParts'
import { CaveUprightBuilder } from './parts/caveUprightParts'
import { CavePowerRackBuilder } from './cavePowerRackBuilder'
import { CaveShelfBuilder } from './parts/caveShelfParts'
import { CaveCableSystemBuilder } from './parts/caveCableSystems'
import { computeShelfOffsets } from '../../utils/shelf-offsets'
import { BarBuilder } from '../bar/barBuilder'
import { BAR_CONFIG } from './utils/caveConstants'

export class CaveBuilder {
  protected config: CaveOptions

  // Extracted config values
  protected line: CaveOptions['line']
  protected height: CaveOptions['height']
  protected foldBackDepth: CaveOptions['foldBackDepth']
  protected hardware: CaveOptions['hardware']
  protected leftCableSystem: CaveOptions['leftCableSystem']
  protected rightCableSystem: CaveOptions['rightCableSystem']

  protected isRML: boolean
  protected isRM: boolean
  protected strapSafetyLength: string
  protected leftJCup: PartDefinition | undefined

  // Materials
  protected material: MaterialInfo

  // Delegate builders
  protected uprightBuilder: CaveUprightBuilder
  protected crossmemberBuilder: CaveCrossmemberBuilder
  protected accessoryBuilder: CaveAccessoryBuilder
  protected cableSystemBuilder: CaveCableSystemBuilder
  protected materialBuilder: MaterialBuilder
  protected shelfBuilder: CaveShelfBuilder

  constructor(config: CaveOptions) {
    this.config = config
    this.line = config.line
    this.height = config.height
    this.foldBackDepth = config.foldBackDepth
    this.hardware = config.hardware
    this.leftCableSystem = config.leftCableSystem
    this.rightCableSystem = config.rightCableSystem
    this.materialBuilder = new MaterialBuilder(config)

    // Compute derived values
    this.isRML = this.line === 'RML'
    this.isRM = this.line === 'RM'
    this.strapSafetyLength = this.foldBackDepth.substring(0, 1)

    this.material = this.materialBuilder.createMainMaterial()

    // Initialize delegate builders
    this.uprightBuilder = new CaveUprightBuilder(config, this.material)
    this.crossmemberBuilder = new CaveCrossmemberBuilder(config, this.material)
    this.accessoryBuilder = new CaveAccessoryBuilder(config, this.material, this.materialBuilder)
    this.cableSystemBuilder = new CaveCableSystemBuilder(config, this.material)
    this.shelfBuilder = new CaveShelfBuilder(config, this.material)
  }

  public build(): PartDefinition {
    return this.assembleRack()
  }

  protected createMainMaterial(): MaterialInfo {
    return {
      main     : { material: 'mg', color: this.config.color },
      hardware : { material: this.config.hardware },
    }
  }

  // MARK: Assemble
  protected assembleRack(): PartDefinition {
    // Build the main squat rack section
    const mainRack = this.buildSquatRackSection()

    // Add pullup bar connecting the sides
    if (this.config.mainRackType === 'fold') {
      this.accessoryBuilder.createFoldBackPullupBar(mainRack)
    }

    // Add the left shelf section
    if (this.config.addLeftSide) {
      this.attachShelfSection(mainRack, true)
    }
  
    // Add the right shelf section
    if (this.config.addRightSide) {
      this.attachShelfSection(mainRack, false)
    }

    // Allows the user to visualize a bar with 135# on the jcups
    if (this.config.showEquipped && this.leftJCup) {
      this.leftJCup.slots.bar = new BarBuilder(BAR_CONFIG).build()
    }

    return mainRack
  }

  protected getFrontUpright(mainRack: PartDefinition, left: boolean): PartDefinition {
    return left ? mainRack : mainRack.slots['other-side'] as PartDefinition
  }

  protected getBackUpright(mainRack: PartDefinition, left: boolean): PartDefinition {
    // 3W
    if (this.config.mainRackType === 'fold') {
      const side = left ? mainRack : mainRack.slots['other-side'] as PartDefinition
      const crossmember = side.slots['crossmember-bottom'] as PartDefinition
      let hinge = crossmember?.slots.back as PartDefinition

      // Full W
      if (this.config.foldBackType === 'full') {
        const topSevenInchCrossmember = hinge?.slots['crossmember-bottom'] as PartDefinition
        hinge = topSevenInchCrossmember?.slots['back'] as PartDefinition
      }

      return hinge.slots.top as PartDefinition
    }

    // RM-6
    if (this.config.mainRackType === 'fixed' && this.config.sixPost) {
      const side = left ? mainRack : mainRack.slots['other-side'] as PartDefinition
      const crossmember = side.slots['crossmember-bottom'] as PartDefinition[]
      const backUpright = crossmember[0]?.slots.back as PartDefinition
      const storageCrossmember = backUpright.slots['crossmember-bottom'] as PartDefinition
      const storageUpright = storageCrossmember.slots['back'] as PartDefinition
    
      return storageUpright
    }

    // Default to the RM-4
    const side = left ? mainRack : mainRack.slots['other-side'] as PartDefinition
    const crossmember = side.slots['crossmember-bottom'] as PartDefinition[]
    const backUpright = crossmember[0]?.slots.back as PartDefinition
    
    return backUpright
  }

  // MARK: MAIN Rack
  protected buildSquatRackSection(): PartDefinition {
    switch (this.config.mainRackType) {
      case 'fold':
        return this.buildFoldBackRack()
      case 'fixed': {
        const powerRackBuilder = new CavePowerRackBuilder(this.config)
        const rack = powerRackBuilder.build()
        this.leftJCup = powerRackBuilder.leftJCup
        
        return rack
      }
      default:
        return this.buildFoldBackRack()
    }
  }

  // Build 3W and Full W
  protected buildFoldBackRack(): PartDefinition {
    if (this.config.foldBackType === '3w') {
      // Build LEFT side
      const leftFront = this.uprightBuilder.createFrontStick(true)
      const leftBack = this.uprightBuilder.createBackUpright(true)
      this.build3WRack(leftFront, leftBack, true)

      // Build RIGHT side
      const rightFront = this.uprightBuilder.createFrontStick(false)
      const rightBack = this.uprightBuilder.createBackUpright(false)
      this.build3WRack(rightFront, rightBack, false)

      // Connect sides
      leftFront.slots['other-side'] = rightFront

      return leftFront
    } 

    // Build LEFT side
    const leftFront = this.uprightBuilder.createFrontStick(true)
    const leftBackStick = this.uprightBuilder.createFullWBackStick(true)
    const leftBackUpright = this.uprightBuilder.createBackUpright(true)
    this.buildFullWRack(leftFront, leftBackStick, leftBackUpright)
    this.addFoldBackRackAccessories(leftFront, leftBackStick, leftBackUpright, true)

    // Build RIGHT side
    const rightFront = this.uprightBuilder.createFrontStick(false)
    const rightBackStick = this.uprightBuilder.createFullWBackStick(false)
    const rightBackUpright = this.uprightBuilder.createBackUpright(false)
    this.buildFullWRack(rightFront, rightBackStick, rightBackUpright)
    this.addFoldBackRackAccessories(rightFront, rightBackStick, rightBackUpright, false)

    // Connect sides
    leftFront.slots['other-side'] = rightFront

    return leftFront
  }

  // MARK: Full W
  protected buildFullWRack(frontStick: PartDefinition, backStick: PartDefinition, backUpright: PartDefinition): void {
    const bottomRackCrossmember = this.crossmemberBuilder.createBottomFullWCrossmember()
    const topRackCrossmember = this.crossmemberBuilder.createTopFullWCrossmember()
    const bottomElevenInchCrossmember = this.crossmemberBuilder.createBottomElevenInchCrossmember()
    const topElevenInchCrossmember = this.crossmemberBuilder.createTopElevenInchCrossmember()
    const bottomHinge = this.crossmemberBuilder.createBottomHingeAssembly()
    const topHinge = this.crossmemberBuilder.createTopHingeAssembly()

    frontStick.slots['crossmember-bottom'] = bottomRackCrossmember
    bottomRackCrossmember.slots.back = backStick
    backStick.slots['crossmember-bottom'] = bottomElevenInchCrossmember
    bottomElevenInchCrossmember.slots.back = bottomHinge
    bottomHinge.slots.top = backUpright

    // Add safeties
    if (this.config.safeties) {
      frontStick.slots['safety-high'] = this.accessoryBuilder.createSafeties()
    }

    // Connect top
    frontStick.slots.top = topRackCrossmember
    backStick.slots['crossmember-top'] = topElevenInchCrossmember
    topElevenInchCrossmember.slots.back = topHinge
  }

  // MARK: 3W
  protected build3WRack(frontStick: PartDefinition, backUpright: PartDefinition, left: boolean): void {
    const bottomFoldingBack = this.crossmemberBuilder.createBottomFoldingBackCrossmember()
    const topFoldingBack = this.crossmemberBuilder.createTopFoldingBackCrossmember()
    const bottomHinge = this.crossmemberBuilder.createBottomHingeAssembly()
    const topHinge = this.crossmemberBuilder.createTopHingeAssembly()

    frontStick.slots['crossmember-bottom'] = bottomFoldingBack
    bottomFoldingBack.slots.back = bottomHinge
    bottomHinge.slots.top = backUpright
    frontStick.slots.top = topFoldingBack
    topFoldingBack.slots.back = topHinge

    // Add jcups and nameplate
    this.addFoldBackRackAccessories(frontStick, backUpright, backUpright, left)
  }

  // MARK: Rack Sides
  protected attachShelfSection(mainRack: PartDefinition, left: boolean): void {
    const isSelected = left ? this.config.isLeftSideAdded : this.config.isRightSideAdded
    if (!isSelected) return

    const backUpright = this.getBackUpright(mainRack, left)
    const shelfUpright = this.uprightBuilder.createShelfUpright(left)
    const cableUpright = this.uprightBuilder.createCableUpright(left)

    this.addShelves(shelfUpright, left)

    this.buildShelfSection(backUpright, shelfUpright, cableUpright, left)
  }

  protected buildShelfSection(backUpright: PartDefinition, shelfUpright: PartDefinition, cableUpright: PartDefinition, left: boolean): void {
    // Connect storage crossmembers from back upright to shelf upright
    const bottomStorageCrossmember = this.crossmemberBuilder.createBottomStorageCrossmember(left)
    const topStorageCrossmember = this.crossmemberBuilder.createTopStorageCrossmember(left)
    const cableSystem = left ? this.leftCableSystem : this.rightCableSystem
    
    // Builds storage section
    backUpright.slots['crossmember-bottom'] = bottomStorageCrossmember
    backUpright.slots['crossmember-top'] = topStorageCrossmember
    bottomStorageCrossmember.slots.back = shelfUpright

    // Builds pulley section
    const isSlinger = cableSystem?.includes('slinger')
    const bottomPulleyCrossmember = this.crossmemberBuilder.createBottomPulleyCrossmember(left)
    const topPulleyCrossmember = this.crossmemberBuilder.createTopPulleyCrossmember(left)

    // Only add the upright and top crossmember if no selection is made or INDY
    if (cableSystem?.includes('indy')) {
      shelfUpright.slots['crossmember-bottom'] = bottomPulleyCrossmember
      shelfUpright.slots['crossmember-top'] = topPulleyCrossmember
      bottomPulleyCrossmember.slots.front = cableUpright
    } else if (isSlinger) {
      shelfUpright.slots['crossmember-bottom'] = bottomPulleyCrossmember
      shelfUpright.slots['crossmember-top'] = topPulleyCrossmember
      bottomPulleyCrossmember.slots.front = cableUpright

      // Slinger X brings its own shroud/weight stack — only plate storage is Cave-provided.
      const plateStorage = left ? this.config.leftPlateStorage : this.config.rightPlateStorage
      this.assignPlateStorage(cableUpright, left, plateStorage)

      if (cableSystem === 'slinger') {
        // Legacy static-mesh Slinger (SKU: CAVE_OLD_SLINGER_SKU) — remove once the old SKU is retired.
        const seatSelection = this.cableSystemBuilder.getCTM1SeatSelection(left)
        cableUpright.slots['cave-shelf-one'] = seatSelection
          ? this.cableSystemBuilder.createSlingerSeat(left)
          : this.cableSystemBuilder.createSlingerLowPulley(left)

        const showShroud = left ? this.config.leftShrouds : this.config.rightShrouds
        if (showShroud) {
          cableUpright.slots.shroud = this.cableSystemBuilder.createSlingerShroud(left)
        }
      }

    } else if (!cableSystem) {
      shelfUpright.slots['crossmember-bottom'] = this.crossmemberBuilder.createHalfFoot()
    }

    this.addCableSystem(shelfUpright, cableUpright, topPulleyCrossmember, bottomPulleyCrossmember, left)
  }

  // MARK: Cable Systems
  protected addCableSystem(shelfUpright: PartDefinition, cableUpright: PartDefinition, topPulleyCrossmember: PartDefinition, bottomPulleyCrossmember: PartDefinition, left: boolean): void {
    const cableSystem = left ? this.leftCableSystem : this.rightCableSystem
    if (!cableSystem) return
  
    const is100 = this.config.height === '100'

    if (cableSystem === 'ct' && is100) {
      const ct1 = this.cableSystemBuilder.createCT1(left)
      ct1.slots.sheath = this.cableSystemBuilder.createCT1Shroud(left)
      shelfUpright.slots['crossmember-bottom'] = ct1
    } else if (cableSystem === 'lp' && is100) {
      shelfUpright.slots['crossmember-bottom'] = this.cableSystemBuilder.createLP2(left)
    } else if (cableSystem.includes('ctm')) {
      shelfUpright.slots['crossmember-bottom'] = this.cableSystemBuilder.createCTM1(left)
    } else if (cableSystem.includes('slinger') && this.config.height !== '80') {
      if (cableSystem.startsWith('slinger x')) {
        this.cableSystemBuilder.buildSlingerXSystem(cableUpright, topPulleyCrossmember, bottomPulleyCrossmember, left)
      } else {
        // Default branch (not `=== 'slinger'`) so an unrecognized value never falls through to the newer builder.
        bottomPulleyCrossmember.slots.middle = this.cableSystemBuilder.createSlinger(left)
      }
    } else if (cableSystem.includes('indy')) {
      this.cableSystemBuilder.buildIndySystem(topPulleyCrossmember, bottomPulleyCrossmember, left)
    }
  }

  // MARK: Plate Storage
  protected assignPlateStorage(cableUpright: PartDefinition, left: boolean, plateStorage: string | undefined): void {
    if (!plateStorage) return

    switch (plateStorage) {
      case '3 short 2 long':
        cableUpright.slots['cave-shelf-five'] = this.accessoryBuilder.createPlateStorage(left, 'short')
        cableUpright.slots['cave-shelf-four'] = this.accessoryBuilder.createPlateStorage(left, 'long')
        break
      case '4 long':
        cableUpright.slots['cave-shelf-five'] = this.accessoryBuilder.createPlateStorage(left, 'long', 6)
        cableUpright.slots['cave-shelf-four'] = this.accessoryBuilder.createPlateStorage(left, 'long')
        break
      case '3 short':
        cableUpright.slots['cave-shelf-four'] = this.accessoryBuilder.createPlateStorage(left, 'short')
        break
      case '2 long':
        cableUpright.slots['cave-shelf-four'] = this.accessoryBuilder.createPlateStorage(left, 'long')
        break
    }
  }

  // Only used for the Fold Back racks
  protected addFoldBackRackAccessories(frontStick: PartDefinition, backStick: PartDefinition, backUpright: PartDefinition, left: boolean): void {
    const jCup = this.accessoryBuilder.createJCup(left)

    if (left) this.leftJCup = jCup

    // Add jcups to Full W back stick
    if (this.config.foldBackType === 'full') {
      backStick.slots['jcup-high'] = jCup
    } else {
      // Add jcups to the front stick on the 3W
      frontStick.slots['jcup-high'] = jCup
    }

    // Nameplate on left back upright
    if (left) {
      backUpright.slots['safety-high'] = this.accessoryBuilder.createBackCrossmember() as PartDefinition
    }
  }

  // MARK: Shelves
  protected addShelves(shelfUpright: PartDefinition, left: boolean): void {
    const { leftShelves, rightShelves } = this.config.shelfConfig as ShelfConfig
    const shelves = left ? leftShelves : rightShelves
    const panelWidth = left ? this.config.leftPanelWidth : this.config.rightPanelWidth

    const shelfKeys = ['shelf1', 'shelf2', 'shelf3', 'shelf4', 'shelf5'] as const
    const slotNames = ['cave-shelf-one', 'cave-shelf-two', 'cave-shelf-three', 'cave-shelf-four', 'cave-shelf-five']

    const selections = shelfKeys.map(key => shelves?.[key])
    const activeSelections = selections.filter(Boolean)
    const panelCount = selections.filter(s => s === 'panel').length
    const isPanelOnlyConfig = panelCount === 5 && activeSelections.every(s => s === 'panel')
    const offsets = computeShelfOffsets(selections)

    shelfKeys.forEach((key, index) => {
      const shelfSelection = shelves?.[key]

      if (panelWidth === '70' && shelfSelection?.includes('panel')) return

      shelfUpright.slots[slotNames[index]] = this.shelfBuilder.createShelf(shelfSelection, left,offsets[index], isPanelOnlyConfig) as PartDefinition
    })
  }
}